import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../features/auth/authSlice';
import {
  getEffectiveModuleFeatures,
  hasModuleFeature,
  hasWhatsAppModuleAccess,
} from '../../features/auth/moduleFeatures';
import { contactAgentApi } from '../../features/contact-agent/api/contactAgentApi';
import { mesaApi } from '../../features/mesa/api/mesaApi';
import { chatbotAdminApi } from '../../features/chatbot/api/chatbotApi';
import type { ContactAgentDashboardMetrics, HelpdeskStats } from '@workspace/types';

export type DashboardSnapshot = {
  chatbotInstances: number | null;
  contactAgent: ContactAgentDashboardMetrics | null;
  mesa: HelpdeskStats | null;
};

export function useDashboardSnapshot() {
  const user = useSelector(selectCurrentUser);
  const moduleFeatures = getEffectiveModuleFeatures(user);
  const flags = useMemo(
    () => ({
      chatbot: user?.role === 'SUPERADMIN' || moduleFeatures.includes('chatbot'),
      contactAgent: hasModuleFeature(user, 'contact-agent'),
      mesa: hasModuleFeature(user, 'helpdesk'),
      whatsApp: hasWhatsAppModuleAccess(user),
      inbox: user?.role === 'OPERATOR' || user?.role === 'ADMIN' || user?.role === 'SUPERADMIN',
      leads:
        (user?.role === 'ADMIN' || user?.role === 'SUPERADMIN')
        && (user?.role === 'SUPERADMIN' || moduleFeatures.includes('leads')),
    }),
    [moduleFeatures, user],
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snapshot, setSnapshot] = useState<DashboardSnapshot>({
    chatbotInstances: null,
    contactAgent: null,
    mesa: null,
  });

  useEffect(() => {
    if (!user || user.role === 'USER') {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    const tasks: Promise<void>[] = [];

    if (flags.chatbot && user.role !== 'OPERATOR') {
      tasks.push(
        chatbotAdminApi
          .listInstances()
          .then((instances: { id: string }[]) => {
            if (!cancelled) {
              setSnapshot(prev => ({ ...prev, chatbotInstances: instances.length }));
            }
          })
          .catch(() => {
            if (!cancelled) {
              setSnapshot(prev => ({ ...prev, chatbotInstances: null }));
            }
          }),
      );
    }

    if (flags.contactAgent && user.role !== 'OPERATOR') {
      tasks.push(
        contactAgentApi
          .getDashboard()
          .then(metrics => {
            if (!cancelled) {
              setSnapshot(prev => ({ ...prev, contactAgent: metrics }));
            }
          })
          .catch(() => {
            if (!cancelled) {
              setSnapshot(prev => ({ ...prev, contactAgent: null }));
            }
          }),
      );
    }

    if (flags.mesa && user.role !== 'OPERATOR') {
      tasks.push(
        mesaApi
          .getStats()
          .then(stats => {
            if (!cancelled) {
              setSnapshot(prev => ({ ...prev, mesa: stats }));
            }
          })
          .catch(() => {
            if (!cancelled) {
              setSnapshot(prev => ({ ...prev, mesa: null }));
            }
          }),
      );
    }

    void Promise.all(tasks)
      .catch(() => {
        if (!cancelled) {
          setError('No se pudieron cargar algunas métricas.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [flags.chatbot, flags.contactAgent, flags.mesa, user]);

  return { user, flags, loading, error, snapshot };
}
